/**
 * useQuickCertificateShare — One-tap "share certificate to WhatsApp" flow.
 *
 * Renders the default certificate template into a hidden offscreen DOM node,
 * snapshots it to PNG via html2canvas, uploads to the `certificates` storage
 * bucket, inserts a `certificates` row, then opens WhatsApp with the share URL
 * pre-filled.
 *
 * Reuses the same image-generation pipeline as `useNewCertificate.saveCertificate`
 * to guarantee consistent output, but skips all UI steps (template picker,
 * content step, preview, phone gate).
 */
import { useState, useCallback } from 'react';
import { createRoot } from 'react-dom/client';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { useSubscription } from '@/hooks/useSubscription';
import { useToast } from '@/hooks/use-toast';
import { CertificateTemplate } from '@/components/certificate/CertificateTemplate';
import type { Goal } from '@/types/todo';
import {
  getCertificateShareUrl,
  buildCertificateWhatsAppMessage,
} from '@/utils/certificateShare';
import { logger } from '@/utils/logger';

interface QuickShareInput {
  goalId?: string;
  goalTitle: string;
}

function getDisplayName(user: { user_metadata?: any; email?: string | null } | null): string {
  if (!user) return 'Goal Achiever';
  if (user.user_metadata?.full_name) return user.user_metadata.full_name;
  if (user.user_metadata?.display_name) return user.user_metadata.display_name;
  if (user.email) {
    return user.email
      .split('@')[0]
      .split(/[._-]/)
      .map((p) => p.charAt(0).toUpperCase() + p.slice(1))
      .join(' ');
  }
  return 'Goal Achiever';
}

export function useQuickCertificateShare() {
  const { user } = useAuth();
  const { checkCertificateLimit, incrementCertificateCount } = useSubscription();
  const { toast } = useToast();
  const [isSharing, setIsSharing] = useState(false);

  const quickShare = useCallback(
    async ({ goalTitle }: QuickShareInput): Promise<boolean> => {
      if (!user) {
        toast({
          title: 'Please sign in',
          description: 'You need an account to create and share certificates.',
          variant: 'destructive',
        });
        return false;
      }
      if (isSharing) return false;

      setIsSharing(true);

      // Open a placeholder window synchronously so popup blockers (esp. iOS Safari)
      // don't block the WhatsApp redirect after the async work below.
      let shareWindow: Window | null = null;
      try {
        shareWindow = window.open('about:blank', '_blank');
      } catch {
        shareWindow = null;
      }

      let host: HTMLDivElement | null = null;
      try {
        // 1. Quota check
        const limitCheck = await checkCertificateLimit();
        if (!limitCheck.can_create && limitCheck.reason === 'free_limit_reached') {
          if (shareWindow) shareWindow.close();
          toast({
            title: 'Certificate limit reached',
            description: `You've created ${limitCheck.current_count} certificates this month. Upgrade to Premium for unlimited certificates.`,
            variant: 'destructive',
          });
          return false;
        }

        const userName = getDisplayName(user);
        const completionDate = new Date().toISOString().split('T')[0];

        // 2. Render certificate offscreen
        host = document.createElement('div');
        host.setAttribute('aria-hidden', 'true');
        host.style.position = 'fixed';
        host.style.left = '-10000px';
        host.style.top = '0';
        host.style.width = '1024px';
        host.style.height = 'auto';
        host.style.pointerEvents = 'none';
        document.body.appendChild(host);

        const dummyGoal: Goal = {
          id: 'quick',
          title: goalTitle,
          progress: 100,
          dueDate: new Date().toISOString(),
          category: 'personal',
          todos: [],
        } as unknown as Goal;

        const root = createRoot(host);
        root.render(
          <CertificateTemplate
            goal={dummyGoal}
            userName={userName}
            customData={{
              userName,
              goalTitle,
              progress: 100,
              dueDate: completionDate,
              tagline: 'Set your goals and achieve greatness!',
              style: 'formal',
            }}
          />
        );

        // Wait two animation frames + small buffer so the template fully paints
        // (fonts, gradients, decorative elements).
        await new Promise<void>((r) =>
          requestAnimationFrame(() => requestAnimationFrame(() => setTimeout(r, 250)))
        );

        const target = host.firstElementChild as HTMLElement | null;
        if (!target) throw new Error('Certificate failed to render');

        // 3. Snapshot to PNG
        const html2canvas = (await import('html2canvas')).default;
        const canvas = await html2canvas(target, {
          scale: 2,
          useCORS: true,
          backgroundColor: '#ffffff',
          logging: false,
        });
        const blob: Blob = await new Promise((resolve, reject) => {
          canvas.toBlob(
            (b) => (b ? resolve(b) : reject(new Error('Failed to encode image'))),
            'image/png',
            0.95
          );
        });

        // 4. Upload + insert row
        const fileName = `${user.id}/${Date.now()}-quick-certificate.png`;
        const { error: uploadError } = await supabase.storage
          .from('certificates')
          .upload(fileName, blob, { contentType: 'image/png', cacheControl: '3600' });
        if (uploadError) throw uploadError;

        const { data: urlData } = supabase.storage.from('certificates').getPublicUrl(fileName);

        const { data: profile } = await supabase
          .from('profiles')
          .select('subscription_tier')
          .eq('id', user.id)
          .single();
        const isPremium = profile?.subscription_tier === 'premium';
        const expiresAt = isPremium
          ? null
          : new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString();

        const { data: cert, error: certError } = await supabase
          .from('certificates')
          .insert({
            user_id: user.id,
            certificate_title: `${goalTitle} Achievement`,
            user_name: userName,
            goal_title: goalTitle,
            completion_date: completionDate,
            tagline: 'Set your goals and achieve greatness!',
            template_style: 'formal',
            image_url: urlData.publicUrl,
            expires_at: expiresAt,
          })
          .select('id')
          .single();
        if (certError) throw certError;

        await incrementCertificateCount();

        // 5. Open WhatsApp with share link
        const shareUrl = getCertificateShareUrl(cert.id);
        const message = buildCertificateWhatsAppMessage({ goalTitle, userName, shareUrl });
        const whatsappUrl = `https://wa.me/?text=${encodeURIComponent(message)}`;

        if (shareWindow) {
          shareWindow.location.href = whatsappUrl;
        } else {
          window.open(whatsappUrl, '_blank');
        }

        toast({
          title: 'Certificate ready! 🎉',
          description: 'Opening WhatsApp to share your achievement.',
          duration: 4000,
        });

        // Cleanup the offscreen render
        try {
          root.unmount();
        } catch {
          /* ignore */
        }
        return true;
      } catch (err) {
        logger.error('[useQuickCertificateShare] failed:', err);
        if (shareWindow) shareWindow.close();
        toast({
          title: 'Could not create certificate',
          description: 'Something went wrong. Please try again from the celebration screen.',
          variant: 'destructive',
        });
        return false;
      } finally {
        if (host && host.parentNode) host.parentNode.removeChild(host);
        setIsSharing(false);
      }
    },
    [user, isSharing, checkCertificateLimit, incrementCertificateCount, toast]
  );

  return { quickShare, isSharing };
}
